import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  /** Forwarded so the control itself is marked, not just its label. */
  required?: boolean;
}

export interface FieldProps {
  label: string;
  help?: string;
  error?: string;
  /**
   * False renders the message without a live region, so it is not announced. When it
   * flips to true the message is re-inserted: live regions are announced on insertion,
   * and no spec promises an announcement for a role added to an element already in the
   * tree.
   */
  errorLive?: boolean;
  required?: boolean;
  /** Announced after the label; the asterisk alone carries no meaning to a screen reader. */
  requiredLabel?: string;
  className?: string;
  children: (props: FieldControlProps) => React.ReactNode;
}

const Field: React.FC<FieldProps> = ({
  label,
  help,
  error,
  errorLive = true,
  required,
  requiredLabel,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const requiredText = requiredLabel ?? t('common.required');
  const id = React.useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const describedBy = [help ? helpId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {/* Label ships leading-none; kanji needs 1.5 as a floor. */}
      <Label
        htmlFor={id}
        className={cn('revy-cjk text-sm font-medium leading-[1.5]', error && 'text-destructive')}
      >
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-1 text-destructive">
              *
            </span>
            <span className="sr-only">{requiredText}</span>
          </>
        )}
      </Label>

      {children({
        id,
        'aria-describedby': describedBy || undefined,
        'aria-invalid': error ? true : undefined,
        required: required || undefined,
      })}

      {help && (
        <p
          id={helpId}
          className="revy-cjk text-xs leading-[1.7] tracking-[0.02em] text-muted-foreground"
        >
          {help}
        </p>
      )}
      {error && (
        <p
          key={errorLive ? 'live' : 'silent'}
          id={errorId}
          role={errorLive ? 'alert' : undefined}
          className="revy-cjk text-xs font-medium leading-[1.7] tracking-[0.02em] text-destructive"
        >
          {error}
        </p>
      )}
    </div>
  );
};

export { Field };
